import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Archive, Copy, Ellipsis, Pencil } from "lucide-react";
import { toast } from "sonner";

import ACL_RELATIONSHIPS from "acl-relationships";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import Authorized from "components/Authorized";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import { invalidateEmailTemplateTableQuery } from "hooks/useEmailTemplateTable";
import EmailTemplateService from "services/EmailTemplate";
import { EMAIL_TEMPLATES_PATH } from "routes/email/emailTemplateCache";

const EDIT_ACLS = [
  ACL_RELATIONSHIPS.emailTemplatesTags.read,
  ACL_RELATIONSHIPS.emailTemplates.edit,
];
const ARCHIVE_ACLS = [
  ACL_RELATIONSHIPS.emailTemplatesTags.read,
  ACL_RELATIONSHIPS.emailTemplates.delete,
];
const CLONE_ACLS = [
  ACL_RELATIONSHIPS.emailTemplatesTags.read,
  ACL_RELATIONSHIPS.emailTemplates.create,
];
const ANY_ACLS = [
  ACL_RELATIONSHIPS.emailTemplates.edit,
  ACL_RELATIONSHIPS.emailTemplates.delete,
  ACL_RELATIONSHIPS.emailTemplates.create,
];

/**
 * Lists the campaign schedules that send this template. Archiving disables
 * them, so the user sees which ones before confirming.
 */
const ArchiveDescription = ({ templateId, name }) => {
  const { data: usage, isPending } = useQuery({
    queryKey: ["email-template-usage", templateId],
    queryFn: () => EmailTemplateService.checkIfTemplateBeingUsed(templateId),
  });

  if (isPending) return <Spinner />;

  const schedules = usage?.schedule_data ?? [];

  if (schedules.length === 0) {
    return name
      ? `Are you sure you want to archive the email template "${name}"?`
      : "Are you sure you want to archive this email template?";
  }

  // Block spans rather than a list: the dialog description renders a <p>.
  return (
    <>
      <span className="tw:block">This template is scheduled to send in:</span>
      {schedules.map(({ schedule_id, schedule_name, campaign_url }) => (
        <span key={schedule_id} className="tw:block tw:pl-3">
          {`• ${schedule_name} (ID: ${schedule_id}) in this `}
          <Link to={campaign_url} target="_blank">
            campaign
          </Link>
        </span>
      ))}
      <span className="tw:mt-2 tw:block">
        Archiving disables these schedules. Are you sure?
      </span>
    </>
  );
};

export const EmailTemplateTableActions = ({ original }) => {
  const navigate = useNavigate();
  const [archiveOpen, setArchiveOpen] = useState(false);

  const handleArchive = async () => {
    try {
      await EmailTemplateService.deleteEmailTemplate(original.id);
      await invalidateEmailTemplateTableQuery();
      toast.success("Email template archived");
      setArchiveOpen(false);
    } catch {
      toast.error("Failed to archive email template");
    }
  };

  return (
    <Authorized permissions={ANY_ACLS} partialMatch>
      <>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Email template actions"
            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          >
            <Ellipsis />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="tw:w-auto">
            <Authorized permissions={EDIT_ACLS}>
              <DropdownMenuItem
                onClick={() =>
                  navigate(`${EMAIL_TEMPLATES_PATH}/${original.id}`)
                }
              >
                <Pencil />
                Edit
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={CLONE_ACLS}>
              <DropdownMenuItem
                onClick={() =>
                  navigate(`${EMAIL_TEMPLATES_PATH}/new?clone=${original.id}`)
                }
              >
                <Copy />
                Clone
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={ARCHIVE_ACLS}>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setArchiveOpen(true)}
              >
                <Archive />
                Archive
              </DropdownMenuItem>
            </Authorized>
          </DropdownMenuContent>
        </DropdownMenu>
        <Authorized permissions={ARCHIVE_ACLS}>
          <ConfirmDeleteDialog
            open={archiveOpen}
            onOpenChange={setArchiveOpen}
            title="Archive email template?"
            confirmText="Archive"
            description={
              archiveOpen && (
                <ArchiveDescription
                  templateId={original.id}
                  name={original.name}
                />
              )
            }
            onConfirm={handleArchive}
          />
        </Authorized>
      </>
    </Authorized>
  );
};
